import React, { Component, PropTypes } from 'react';
import moment from 'moment';
import { Link } from 'react-router';
import { browserHistory } from 'react-router';
import { isCollaborator } from 'project/ducks/wrapup';
import { canCollaborate } from 'project/utils';
import ApproveModal from 'project/containers/wrapup/approve_modal';
import Money from 'common/components/money';
import MessageTimeline from 'common/containers/message_timeline';
import IncreaseAwardButton from 'project/containers/wrapup/increase_award_button';
import SignContract from 'project/containers/wrapup/sign_contract';

const APPROVE_DESCR = "Looks like you're ready to finalize your project. Before you do, make sure your name reflects exactly what you want. When you click the \"Approve name\" button below, your project will be finished, we'll issue the award to the creative and you will officially own the new name!"
const APPROVE_CONFIRMATION = "I have reviewed the name."


/**
 * 'Final Name' wrapup step.
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */
export default class FinalName extends Component {
  constructor(props) {
    super(props);

    this.handleApprove = this.handleApprove.bind(this);
  }

  componentDidMount() {
    const { wrapup, fetchMessagesIfNeeded } = this.props;

    fetchMessagesIfNeeded({
      streamId: wrapup.name_message_stream,
      wrapupStep: 'approve_name',
      wrapup
    });
  }

  handleApprove() {
    const { wrapup, user, projectUrl } = this.props;
    const isCollab = isCollaborator(wrapup, user);

    /* For collaborators, there's no survey step. Therefore, redirect user to project brief,
       since the current wrapup no longer exists for him. */
    if (isCollab) {
      browserHistory.push(projectUrl);
    }
  }

  renderHeader() {
    const { isCreative, isAdmin, wrapup } = this.props;

    let title, description;

    if (isAdmin) {
      title = <h3>
        Final name for Award { wrapup.award.position }
        <span className="award-value"> (<Money value={wrapup.award.value} />)</span>
      </h3>;

    /* TODO: REMOVE THIS IF BLOCK ONCE WE NO LONGER HAVE LEGACY WRAP-UPS */
    } if (!isCreative && !wrapup.contract.client_signature_created_at) {
      title = <h3>
        Contract and name for Award { wrapup.award.position }
        <span className="award-value"> (<Money value={wrapup.award.value} />)</span>
      </h3>;

      description = <p>
        The contract protects both you and the creative. It ensures you own the creative's work
        and are legally protected.
      </p>;
    } else if (isCreative) {
      title = <h3>
        Contract for Award { wrapup.award.position }
        <span className="award-value"> (<Money value={wrapup.award.value} />)</span>
      </h3>;

      /* contract not yet signed */
      if (!wrapup.contract.creative_signature_created_at) {
        description = <p>
          The contract protects both you and the client. It ensures you are
          compensated for your work and are legally protected. We'll email
          you a signed copy of the contract after you sign it.
        </p>;
      /* contract signed, but name not yet approved */
      } else if (!wrapup.name_approved_at) {
        description = <p>
          We emailed you a <Link target="_blank" to={wrapup.contract.pdf_signed}>signed copy
          of the project contract</Link>.
        </p>;
      /* name approved */
      } else {
        description = <p>
          Your name has been approved.
        </p>;
      }
    } else {
      title = <h3>
        Review and approve name for Award { wrapup.award.position }
        <span className="award-value"> (<Money value={wrapup.award.value} />)</span>
        <IncreaseAwardButton />
      </h3>;

      /* contract not yet signed */
      if (!wrapup.contract.creative_signature_created_at) {
        description = <p>
          { wrapup.creative.username } hasn't signed the contract yet. We'll email you a signed
          copy of the contract when they do.
        </p>;
      /* contract signed, but name not yet approved */
      } else if (!wrapup.name_approved_at) {
        description = <p>
          Please carefully review the name you picked. Once you're happy, click
          the green "Approve name and move to next step" button.
        </p>;
      /* name approved */
      } else {
        description = <p>
          Name has been approved.
        </p>;
      }
    }

    return (
      <div className="row">
        <div className="small-16 large-14 columns">
          <header className="step-header">
            { title }
            { description }
          </header>
        </div>
      </div>
    );
  }

  renderAction({ isClient, isCollab }) {
    const { wrapup, project, toggleApproveModal, isAdmin } = this.props;

    /* name approved */
    if (wrapup.name_approved_at) {
      return (
        <div className="approved text-center">
          <h4 className="text-success"><i className="fa fa-check-circle"/> Approved</h4>
          <label>{ moment(wrapup.name_approved_at).format('MMMM D, YYYY hh:mm a') }</label>
        </div>
      );
    };

    if (isClient || isCollab || isAdmin) {
      /* contract not yet signed */
      if (!wrapup.contract.creative_signature_created_at) {
        return null;
      }

      if (isCollab && !canCollaborate(project, 'approve_name')) {
        return (
          <div className="callout warning">
            { wrapup.creative.username } has signed the contract, but you don't have
            permissions to approve the final name.
          </div>
        );
      }

      return (
        <button className="btn btn-success" onClick={toggleApproveModal}>
          Approve name and move to next step
        </button>
      );
    }

    return null;
  }

  renderContent() {
    const { isCreative, wrapup, details, user, approveChecklist, prepopulatedMsgOpts } = this.props;

    const isClient = user.id == wrapup.client.id;
    const isCollab = isCollaborator(wrapup, user);
    const action = this.renderAction({ isClient, isCollab });

    /* TODO: REMOVE THIS IF BLOCK ONCE WE NO LONGER HAVE LEGACY WRAP-UPS */
    if (!isCreative && !wrapup.contract.client_signature_created_at) {
      return (
        <div className="row">
          <div className="columns small-16">
            <h5 className="text-danger">
              You have not signed the contract. You cannot<br className="hide-for-small-only"/>
              {' '}approve the name until you sign the contract.
            </h5>
            <SignContract wrapup={wrapup} signingAs="client" />
          </div>
        </div>
      );
    }

    /* contract not yet signed (creative view) */
    if (isCreative && !wrapup.contract.creative_signature_created_at) {
      return (
        <div className="row">
          <div className="columns small-16">
            <h5 className="text-danger">
              You have not signed the contract. You cannot<br className="hide-for-small-only"/>
              {' '}be paid until you sign the contract.
            </h5>
            <SignContract wrapup={wrapup} />
          </div>
        </div>
      );
    }

    return (
      <div>
        <div className="row">
          <div className="columns medium-4 small-16">
            <h3>{ isCreative? "Status" : "Name" }</h3>
          </div>
          <div className="columns medium-12 small-16">
            <div className="bundles">
              { isCreative?
              <div>
                { wrapup.name_approved_at?
                <h5 className="text-success">
                  Congratulations! The client approved your name.
                </h5> :
                <h5 className="text-danger">
                  The client has not yet approved your name. We'll let you know as soon
                  as they do.
                </h5> }
              </div> :
              <div>
                { wrapup.contract.creative_signature_created_at?
                <h3>{ wrapup.entry.name }</h3> :
                <h5 className="text-danger">
                  Waiting for { wrapup.creative.username } to sign the contract.
                </h5> }
              </div> }

              { !!action && <div className="btn-container">{ action }</div> }
            </div>
          </div>
        </div>
        <div className="row">
          <div className="columns medium-4 small-16">
            <h3>Messages</h3>
          </div>
          <div className="columns medium-12 small-16">
            <MessageTimeline wrapup={wrapup} canPost={details.available} canAttach={!isCreative}
              canReply={false} editorActionLabel="Send this message" wrapupStep="approve_name"
              editorPrepopulatedOpts={prepopulatedMsgOpts} canPostInternal={!isCreative} />
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { wrapup, approveChecklist, showingApproveModal } = this.props;

    return (
      <div className="wrapup-step final-name">
        { this.renderHeader() }

        { this.renderContent() }

        { showingApproveModal &&
        <ApproveModal wrapup={wrapup} name="name" description={APPROVE_DESCR}
          confirmation={APPROVE_CONFIRMATION} checklist={approveChecklist}
          onDone={this.handleApprove} /> }
      </div>
    );
  }
}

FinalName.propTypes = {
  wrapup: PropTypes.object.isRequired,
  details: PropTypes.object.isRequired,
  project: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  projectUrl: PropTypes.string.isRequired,
  toggleApproveModal: PropTypes.func.isRequired,
  showingApproveModal: PropTypes.bool.isRequired,
  fetchMessagesIfNeeded: PropTypes.func.isRequired,
  approveChecklist: PropTypes.string,
  isCreative: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  prepopulatedMsgOpts: PropTypes.array.isRequired
};



// WEBPACK FOOTER //
// ./project/components/wrapup/final_name.jsx